import * as React from "react";
import { cn } from "@/lib/utils";
import { HelpIcon } from "@/components/ui/help-icon";
import { Label } from "@/components/ui/label";

interface FormSectionProps {
  title: string;
  description?: string;
  info?: string;
  columns?: 1 | 2 | 3;
  children: React.ReactNode;
  className?: string;
}

const columnClasses: Record<1 | 2 | 3, string> = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-3",
};

export function FormSection({
  title,
  description,
  info,
  columns = 2,
  children,
  className,
}: FormSectionProps) {
  return (
    <section className={cn("space-y-3", className)}>
      <div>
        <h3 className="inline-flex items-center text-[13px] font-semibold text-foreground">
          {title}
          {info && <HelpIcon text={info} />}
        </h3>
        {description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <div className={cn("grid gap-x-4 gap-y-3", columnClasses[columns])}>
        {children}
      </div>
    </section>
  );
}

interface FieldProps {
  label: React.ReactNode;
  info?: string;
  error?: string;
  /**
   * Marks the field as mandatory. This is the single convention for required
   * fields — never type a literal "*" into a label.
   */
  required?: boolean;
  /** Compact variant used inside nested cards (dialplan action params). */
  size?: "default" | "sm";
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
}

export function Field({
  label,
  info,
  error,
  required,
  size = "default",
  htmlFor,
  className,
  children,
}: FieldProps) {
  const sm = size === "sm";
  return (
    <div className={cn(sm ? "space-y-0.5" : "space-y-1.5", className)}>
      <Label
        htmlFor={htmlFor}
        className={cn("inline-flex items-center", sm ? "text-[10px]" : "text-[13px]")}
      >
        {label}
        {required && (
          <span className="ml-0.5 text-destructive-text" title="Required" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
        {info && <HelpIcon text={info} />}
      </Label>
      {children}
      {error && (
        <p role="alert" className={cn("text-destructive-text", sm ? "text-[10px]" : "text-xs")}>
          {error}
        </p>
      )}
    </div>
  );
}

interface ValidationSummaryProps {
  /** Human-readable problems that block saving. Empty = form is valid. */
  errors: string[];
  title?: string;
  className?: string;
}

/**
 * Explains why the Save button is disabled. Render it near the bottom of a
 * form so a blocked user always has a reason and a checklist.
 */
export function ValidationSummary({
  errors,
  title,
  className,
}: ValidationSummaryProps) {
  const unique = Array.from(new Set(errors));
  if (unique.length === 0) return null;
  return (
    <div
      role="alert"
      className={cn(
        "rounded-sm border border-destructive-border bg-destructive-subtle px-3 py-2",
        className
      )}
    >
      <p className="text-xs font-medium text-destructive-text">
        {title ??
          `Fix ${unique.length} ${unique.length === 1 ? "issue" : "issues"} before saving`}
      </p>
      <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-destructive-text">
        {unique.map((e) => (
          <li key={e}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

interface FieldNoteProps {
  variant?: "muted" | "warning";
  children: React.ReactNode;
  className?: string;
}

/** Non-blocking inline note, e.g. a footgun warning on a legal-but-risky value. */
export function FieldNote({ variant = "muted", children, className }: FieldNoteProps) {
  return (
    <p
      className={cn(
        "text-xs",
        variant === "warning" ? "text-warning-text" : "text-muted-foreground",
        className
      )}
    >
      {children}
    </p>
  );
}
